import { Switch, Route, Redirect, useLocation } from "wouter";
import { lazy, Suspense, useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import ConsentBanner from "@/components/ConsentBanner";
import FloatingCTA from "@/components/FloatingCTA";
import { ViewingLeadProvider } from "@/contexts/ViewingLeadContext";
import { captureAttribution } from "@/lib/attribution";
import { captureEntryLandingPage } from "@/lib/landingPage";
import Home from "@/pages/Home";

// Every route other than "/" is code-split so visitors to the home page
// (the primary ad-landing destination) never download JS for pages they
// didn't request. This was previously bundled eagerly and inflated the
// initial payload with ~9 unused page bundles.
const About = lazy(() => import("@/pages/About"));
const Services = lazy(() => import("@/pages/Services"));
const Booking = lazy(() => import("@/pages/Booking"));
const ContactPage = lazy(() => import("@/pages/Contact"));
const BusinessUse = lazy(() => import("@/pages/BusinessUse"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const VisitorPassConfirmation = lazy(() => import("@/pages/VisitorPassConfirmation"));
const NotFound = lazy(() => import("@/pages/not-found"));

const PrivateGymLanding = lazy(() => import("@/pages/PrivateGymLanding"));
const BusyProfessionals = lazy(() => import("@/pages/BusyProfessionals"));
const WomenConfidence = lazy(() => import("@/pages/WomenConfidence"));
const QuietGym = lazy(() => import("@/pages/QuietGym"));
const PersonalTrainers = lazy(() => import("@/pages/PersonalTrainers"));
const PostpartumMums = lazy(() => import("@/pages/PostpartumMums"));
const Corporate = lazy(() => import("@/pages/Corporate"));
const TDEECalculator = lazy(() => import("@/pages/TDEECalculator"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const BasepointAccess = lazy(() => import("@/pages/BasepointAccess"));
const BasepointThankYou = lazy(() => import("@/pages/BasepointThankYou"));
const TvDisplay = lazy(() => import("@/pages/TvDisplay"));
const SlimmingClubs = lazy(() => import("@/pages/SlimmingClubs"));
const BackToTraining = lazy(() => import("@/pages/BackToTraining"));
const SeeTheGym = lazy(() => import("@/pages/SeeTheGym"));
const FirstSession = lazy(() => import("@/pages/FirstSession"));
const FirstSessionConfirmation = lazy(() => import("@/pages/FirstSessionConfirmation"));
const Waitlist = lazy(() => import("@/pages/Waitlist"));
const GymBournemouth = lazy(() => import("@/pages/GymBournemouth"));
const BournemouthAirport = lazy(() => import("@/pages/BournemouthAirport"));
const Poole = lazy(() => import("@/pages/Poole"));
const GymPoole = lazy(() => import("@/pages/GymPoole"));
const SafetyDocuments = lazy(() => import("@/pages/SafetyDocuments"));
const GolfTraining = lazy(() => import("@/pages/GolfTraining"));
const ChooseHomeClub = lazy(() => import("@/pages/ChooseHomeClub"));
const BookViewing = lazy(() => import("@/pages/BookViewing"));
const HyroxGymPoole = lazy(() => import("@/pages/HyroxGymPoole"));
const ViewingConfirmed = lazy(() => import("@/pages/ViewingConfirmed"));
const AdminViewing = lazy(() => import("@/pages/AdminViewing"));
const OpenDay = lazy(() => import("@/pages/OpenDayClosed"));
const Membership = lazy(() => import("@/pages/Membership"));
const OpenDayConfirmed = lazy(() => import("@/pages/OpenDayConfirmed"));
const OpenDayBookingTerms = lazy(() => import("@/pages/OpenDayBookingTerms"));

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

// Booking-management routes (/viewing-confirmed/:token) carry the booking's
// own bearer token - the secret that authorizes viewing, cancelling and
// rescheduling it - in the URL path. Never report a PageView (or any other
// pixel event) for those routes: Meta Pixel reads the current page URL for
// every event it sends, so firing it here would hand that credential to a
// third party.
function isSensitiveAnalyticsRoute(pathname: string): boolean {
  return pathname.startsWith('/viewing-confirmed/') || pathname === '/open-day/confirmed' || /^\/tv\/?$/.test(pathname);
}

function MetaPixelPageView() {
  const [location] = useLocation();
  useEffect(() => {
    if (isSensitiveAnalyticsRoute(location)) return;
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location]);
  return null;
}

// Captures marketing attribution (UTMs/click IDs/referrer) on the initial
// landing and on any SPA navigation whose URL carries new tracking params
// (e.g. an internal link to another ad-landing page). Runs once per
// location change; captureAttribution() itself is a no-op unless there's a
// genuine new-touch signal, so plain in-site browsing doesn't churn it.
function AttributionCapture() {
  const [location] = useLocation();
  useEffect(() => {
    if (/^\/tv\/?$/.test(location)) return;
    captureAttribution();
    captureEntryLandingPage();
  }, [location]);
  return null;
}

// wouter does client-side routing via pushState, so the browser's native
// "scroll to #hash on load" behaviour never fires when navigating between
// pages (e.g. /poole#signup). Lazy-loaded route components also aren't in
// the DOM yet on the first render after navigation, so we poll briefly for
// the target element instead of a single scrollIntoView call.
function ScrollToHash() {
  const [location] = useLocation();
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = hash.slice(1);
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      attempts += 1;
      if (attempts < 20) {
        setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
  }, [location]);
  return null;
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-primary text-lg">Loading...</div>
    </div>
  );
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/about" component={About} />
      <Route path="/services" component={Services} />
      <Route path="/booking" component={Booking} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/business-use" component={BusinessUse} />
      <Route path="/private-gym-bournemouth" component={PrivateGymLanding} />
      <Route path="/how-it-works" component={HowItWorks} />
      <Route path="/visitor-pass-confirmation" component={VisitorPassConfirmation} />
      <Route path="/gym-for-busy-professionals-bournemouth" component={BusyProfessionals} />
      <Route path="/private-gym-for-women-bournemouth" component={WomenConfidence} />
      <Route path="/quiet-alternative-to-big-gyms-bournemouth" component={QuietGym} />
      <Route path="/personal-trainer-gym-space-bournemouth" component={PersonalTrainers} />
      <Route path="/postpartum-gym-bournemouth" component={PostpartumMums} />
      <Route path="/corporate" component={Corporate} />
      <Route path="/tdee-calculator" component={TDEECalculator} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/thank-you" component={ThankYou} />
      <Route path="/private-access-basepoint" component={BasepointAccess} />
      <Route path="/basepoint-access/thank-you" component={BasepointThankYou} />
      <Route path="/tv" component={TvDisplay} />
      <Route path="/quiet-private-gym" component={SlimmingClubs} />
      <Route path="/first-session" component={FirstSession} />
      <Route path="/first-session/poole" component={FirstSession} />
      <Route path="/first-session/bournemouth" component={FirstSession} />
      <Route path="/first-session/confirmation" component={FirstSessionConfirmation} />
      <Route path="/back-to-training" component={BackToTraining} />
      <Route path="/see-the-gym" component={SeeTheGym} />
      <Route path="/new-location" component={Waitlist} />
      <Route path="/gym-bournemouth" component={GymBournemouth} />
      <Route path="/gym-bournemouth-airport" component={BournemouthAirport} />
      <Route path="/poole" component={Poole} />
      <Route path="/gym-poole" component={GymPoole} />
      <Route path="/choose-your-home-club" component={ChooseHomeClub} />
      <Route path="/membership" component={Membership} />
      <Route path="/book-viewing" component={BookViewing} />
      <Route path="/book-viewing/poole" component={BookViewing} />
      <Route path="/book-viewing/bournemouth" component={BookViewing} />
      <Route path="/hyrox-gym-poole" component={HyroxGymPoole} />
      {/* /hyrox is a short alias, not a second indexable copy - redirect
          rather than render the same page under two canonical URLs. */}
      <Route path="/hyrox">
        <Redirect to="/hyrox-gym-poole" />
      </Route>
      {/* :token? (optional) so the in-page URL strip in ViewingConfirmed.tsx -
          which rewrites this to bare /viewing-confirmed via history.replaceState
          once the token has been captured - still matches this same route
          instead of falling through to NotFound and unmounting the page. */}
      <Route path="/viewing-confirmed/:token?" component={ViewingConfirmed} />
      <Route path="/admin" component={AdminViewing} />
      <Route path="/safety-documents" component={SafetyDocuments} />
      <Route path="/open-day" component={OpenDay} />
      <Route path="/open-day/confirmed" component={OpenDayConfirmed} />
      <Route path="/open-day/booking-terms" component={OpenDayBookingTerms} />
      <Route path="/golf" component={GolfTraining} />
      <Route component={NotFound} />
    </Switch>
  );
}

function ConditionalPageOverlays() {
  const [location] = useLocation();
  // Internal gym screens must not mount consent UI or visitor-booking prompts.
  if (/^\/tv\/?$/.test(location)) return null;
  return (
    <>
      <ConsentBanner />
      {location !== "/new-location" && <FloatingCTA />}
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ViewingLeadProvider>
          <Toaster />
          <ConditionalPageOverlays />
          <MetaPixelPageView />
          <AttributionCapture />
          <ScrollToHash />
          <Suspense fallback={<PageLoader />}>
            <Router />
          </Suspense>
        </ViewingLeadProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
